import { useState, useEffect, useCallback } from 'react';
import { getAllUsers, deleteUser as deleteUserFromDb, updateUserRole, getPatientsBatch } from '../firebase';
import { User, UserManagementUser, Patient } from '../types';
import { isSuperAdmin } from '../utils/roleUtils';
import { calculateIntakeCompletion } from '../utils/intakeCompletion';

export function useUsers(user: User | null, patients: Patient[] = []) {
  const [users, setUsers] = useState<UserManagementUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserManagementUser[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [userIntakeCompletions, setUserIntakeCompletions] = useState<{[userId: string]: number}>({});

  const calculateIntakeCompletions = useCallback(async (usersList: UserManagementUser[]) => {
    if (patients.length === 0) {
      setUserIntakeCompletions({});
      return;
    }

    try {
      // Get all active cases
      const activeCases = patients.filter(p => p.status === 'active');
      
      if (activeCases.length === 0) {
        setUserIntakeCompletions({});
        return;
      }

      // Get all patient data at once
      const caseIds = activeCases.map(p => p.caseId);
      const patientsData = await getPatientsBatch(caseIds);

      // Import form management functions once
      const { getAllForms, getCaseSubmissions } = await import('../firebase/formManagement');
      const allForms = await getAllForms();
      
      // Calculate completion for each user
      const completions: {[userId: string]: number} = {};

      for (const userItem of usersList) {
        // Get active cases assigned to this user
        const userActiveCases = activeCases.filter(p => 
          Array.isArray(p.assignedAdmins) && 
          p.assignedAdmins.includes(userItem.id)
        );

        if (userActiveCases.length === 0) {
          completions[userItem.id] = 0;
          continue;
        }

        // Calculate average completion for all user's active cases using form-based calculation
        // Collect all form completions from all cases and average them
        const allFormCompletions: number[] = [];
        
        // Get form completions from all cases assigned to this user
        for (const patient of userActiveCases) {
          try {
            const submissions = await getCaseSubmissions(patient.caseId);
            
            for (const form of allForms) {
              const submission = submissions.find(s => s.formId === form.id);
              if (submission && submission.answers && form.fields && form.fields.length > 0) {
                // Calculate completion percentage for this form
                const totalFields = form.fields.length;
                let filledFields = 0;
                
                form.fields.forEach(field => {
                  const fieldName = field.field_name || field.id;
                  const value = submission.answers[fieldName];
                  
                  // Check if field is filled
                  if (value !== undefined && value !== null && value !== '') {
                    if (Array.isArray(value)) {
                      if (value.length > 0) {
                        filledFields++;
                      }
                    } else {
                      filledFields++;
                    }
                  }
                });
                
                const completionPercentage = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
                
                // Only include forms that have been filled (completion > 0)
                if (completionPercentage > 0) {
                  allFormCompletions.push(completionPercentage);
                }
              }
            }
          } catch (error) {
            console.error(`Error calculating form completion for case ${patient.caseId}:`, error);
          }
        }
        
        // Calculate average of all filled forms across all cases
        if (allFormCompletions.length === 0) {
          completions[userItem.id] = 0;
        } else {
          const totalCompletion = allFormCompletions.reduce((sum, comp) => sum + comp, 0);
          const averageCompletion = Math.round(totalCompletion / allFormCompletions.length);
          completions[userItem.id] = averageCompletion;
        }
      }

      setUserIntakeCompletions(completions);
    } catch (error) {
      console.error('Error calculating intake completions:', error);
      setUserIntakeCompletions({});
    }
  }, [patients]);

  const loadUsers = useCallback(async () => {
    if (!user || !isSuperAdmin(user)) return;
    
    try {
      const usersData = await getAllUsers();
      const convertedUsers: UserManagementUser[] = usersData.map(userData => ({
        id: userData.userId,
        name: userData.name,
        email: userData.email,
        role: userData.role,
        organizationId: userData.organizationId,
        organizationRole: userData.organizationRole,
        legacyRole: userData.legacyRole,
        createdAt: userData.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        loginCount: userData.loginCount || 0,
        twoFactorEnabled: userData.twoFactorEnabled || false,
        authDisabled: userData.authDisabled || false,
        blocked: userData.blocked || false,
        restricted: userData.restricted || false,
        blockedReason: userData.blockedReason || ''
      }));
      setUsers(convertedUsers);
      
      // Calculate intake completions
      await calculateIntakeCompletions(convertedUsers);
      
      // Re-apply current filters
      let filtered = convertedUsers;
      
      if (userStatusFilter === 'active') {
        filtered = filtered.filter(u => !u.blocked && !u.restricted);
      } else if (userStatusFilter === 'blocked') {
        filtered = filtered.filter(u => u.blocked || u.restricted);
      }
      
      if (userSearchTerm) {
        const searchLower = userSearchTerm.toLowerCase();
        filtered = filtered.filter(u => 
          u.name?.toLowerCase().includes(searchLower) ||
          u.email?.toLowerCase().includes(searchLower)
        );
      }
      
      setFilteredUsers(filtered);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, [user, userStatusFilter, userSearchTerm, calculateIntakeCompletions]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleUserSearchChange = useCallback((searchTerm: string) => {
    setUserSearchTerm(searchTerm);
    
    let filtered = users;
    
    if (userStatusFilter === 'active') {
      filtered = filtered.filter(u => !u.blocked && !u.restricted);
    } else if (userStatusFilter === 'blocked') {
      filtered = filtered.filter(u => u.blocked || u.restricted);
    }
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(u => 
        u.name?.toLowerCase().includes(searchLower) ||
        u.email?.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredUsers(filtered);
  }, [users, userStatusFilter]);

  const handleUserStatusFilterChange = useCallback((filter: string) => {
    setUserStatusFilter(filter);
    
    let filtered = users;
    
    if (filter === 'active') {
      filtered = filtered.filter(u => !u.blocked && !u.restricted);
    } else if (filter === 'blocked') {
      filtered = filtered.filter(u => u.blocked || u.restricted);
    }
    
    if (userSearchTerm) {
      const searchLower = userSearchTerm.toLowerCase();
      filtered = filtered.filter(u => 
        u.name?.toLowerCase().includes(searchLower) ||
        u.email?.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredUsers(filtered);
  }, [users, userSearchTerm]);

  const handleDeleteUser = useCallback(async (userId: string) => {
    if (!user) return { success: false };
    
    try {
      const result = await deleteUserFromDb(userId);
      if (result.success) {
        await loadUsers();
      }
      return result;
    } catch (error) {
      console.error('Error deleting user:', error);
      return { success: false };
    }
  }, [user, loadUsers]);

  const handleUserStatusChange = useCallback(
    async (userId: string, status: 'active' | 'blocked') => {
      try {
        const currentUser = users.find(u => u.id === userId);
        if (!currentUser) {
          return { success: false };
        }

        const roleToUpdate = currentUser.role === 'user' && currentUser.organizationRole
          ? currentUser.organizationRole
          : (currentUser.role === 'super_admin' ? 'super_admin' : 'admin');

        const result = await updateUserRole(
          userId, 
          roleToUpdate as any,
          currentUser.name,
          currentUser.email,
          currentUser.organizationId,
          status
        );

        if (result.success) {
          await loadUsers();
        }
        return result;
      } catch (error: any) {
        console.error('Error updating user status:', error);
        return { success: false, message: error.message };
      }
    },
    [users, loadUsers]
  );

  // Recalculate completions when patients change
  useEffect(() => {
    if (users.length > 0 && patients.length > 0) {
      calculateIntakeCompletions(users);
    }
  }, [patients, users, calculateIntakeCompletions]);

  return {
    users,
    filteredUsers,
    userSearchTerm,
    userStatusFilter,
    userIntakeCompletions,
    handleUserSearchChange,
    handleUserStatusFilterChange,
    handleDeleteUser,
    handleUserStatusChange,
    refreshUsers: loadUsers
  };
}


